import React, { useEffect, useState } from 'react';

import styled from '@emotion/styled';

import { kycStatusDataActions } from '../../redux/slices/kycStatusSlice';
import { useAppDisPatch, useAppSelector } from '../../redux/store';
import CautionIcon from '../Icons/CautionIcon';
import { Modal } from '../Modal/Modal';
import KycInformationModal from '../Modal/ModalActions/KycInformationModal/KycInformationModal';

interface FullScreenBannerProps {}

const FullScreenBanner: React.FC<FullScreenBannerProps> = () => {
  const dispatch = useAppDisPatch();
  const { kycStatusData, showKycBanner, isKycStatusLoaded } = useAppSelector(
    (state) => state.kycStatusData,
  );
  const [showModal, setShowModal] = useState(false);

  const descriptionText = {
    Rejected: 'KYC document not approved. Check your email for more details',
    Declined: 'KYC document not approved. Check your email for more details',
    Pending: `We're reviewing your information and will notify you once it's approved`,
    'Not Initialized':
      'Just a step away. Complete your KYC to gain full access to your dashboard.',
    Required:
      'Just a step away. Complete your KYC to gain full access to your dashboard.',
  };

  const titleText = {
    Rejected: 'KYC Verification Failed',
    Declined: 'KYC Verification Failed',
    Pending: 'Your account is under review',
    'Not Initialized': 'KYC Verification Required',
    Required: 'KYC Verification Required',
  };

  const buttonText = {
    Rejected: 'Resubmit',
    Declined: 'Resubmit',
    Pending: 'Dismiss',
    'Not Initialized': 'Get Started',
    Required: 'Get Started',
  };

  const onButtonClick = () => {
    if (kycStatusData?.kycStatus === 'Pending') {
      dispatch(kycStatusDataActions.setShowKycBanner(false));
    } else {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (kycStatusData?.kycStatus === 'Accepted' && showKycBanner) {
      dispatch(kycStatusDataActions.setShowKycBanner(false));
    }
  }, [dispatch, kycStatusData?.kycStatus, showKycBanner]);

  // wait for the KYC status fetch to resolve before rendering, so it doesn't flash on refresh
  if (
    !isKycStatusLoaded ||
    !showKycBanner ||
    kycStatusData?.kycStatus === 'Accepted'
  ) {
    return null;
  }

  const kycStatusKey = kycStatusData?.kycStatus as keyof typeof descriptionText;

  return (
    <Container>
      <CautionIcon />
      <TextSection>
        <Title>{titleText[kycStatusKey]}</Title>
        <Description>{descriptionText[kycStatusKey]}</Description>
      </TextSection>
      {buttonText && (
        <Button onClick={onButtonClick}>{buttonText[kycStatusKey]}</Button>
      )}

      <Modal showModal={showModal} setShowModal={setShowModal}>
        <KycInformationModal />
      </Modal>
    </Container>
  );
};

export default FullScreenBanner;

const Container = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr auto;
  gap: 12px;
  padding: 12px 16px;
  box-shadow: 0px 4px 8px 0px #00000040;
  background-color: #474747;
  color: white;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
`;

const Description = styled.div`
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
`;

const Button = styled.button`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: fit-content;
  padding: 6.5px 12px;
  font-weight: 400;
  font-size: 14px;
  background-color: ${({ theme }) => theme.palette.bgWhiteColor};
  color: ${({ theme }) => theme.palette.neutralGray};
  border: 1px solid ${({ theme }) => theme.palette.grayBorder};
  border-radius: 30px;
`;
